import React, { useEffect, useRef, useState } from 'react';
import useGlobalVariables from '../../../hooks/useGlobalVariables';
import useTranslations from '../../../hooks/useTranslations';
import { UPDATE_SCORE_FIELD } from '../../../store/modules/state/formBuilder';
import ScoreSettingsPanelView from '../view/ScoreSettingsPanelView';

const UI_TYPES_ALLOWED_FOR_SCORING = [
  'currency',
  'dropdown',
  'number',
  'section_break',
  'yes_no'
];
const SUBFIELDS_NOT_ALLOWED = [
  'worldcheck',
  'worldcheck_org',
  'credas',
  'credas_out_of_scope'
];

const ALLOWED_WORLDCHECK_SUBFIELDS = [
  'countryLocation',
  'placeOfBirth',
  'nationality',
  'registeredCountry'
];

const ScoreSettingsPanelEngine = ({
  allFields,
  autosaveField,
  internalId,
  setShowScoreDialog,
  ...props
}) => {
  const t = useTranslations();
  const { setIsLoading } = useGlobalVariables();
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [selectedFields, setSelectedFields] = useState([null]);
  const [averageFieldObjects, setAverageFieldObjects] = useState({});
  const uniqueID = useRef(null);
  const [selectedOperationObjects, setSelectedOperationObjects] = useState([]);
  const [formulaArray, setFormulaArray] = useState([]);
  const [initialFormulaArray, setInitialFormulaArray] = useState([]);
  const [validationError, setValidationError] = useState(false);

  // initialization
  useEffect(() => {
    let matchingFields,
      field,
      formulaString,
      formulaList,
      newFields,
      newOperationObjects,
      newAverageFieldObjects,
      fieldIndex;
    setIsLoading(true);
    matchingFields = allFields.filter(one => one.internalId === internalId);
    if (!matchingFields || !matchingFields.length) {
      return;
    }
    field = matchingFields[0];
    formulaString = '[]';
    if (field && field.calculationInfo && field.calculationInfo.formula) {
      formulaString = field.calculationInfo.formula;
    }
    if (!formulaString || formulaString === '[]') {
      setSelectedFields([null]);
      setSelectedOperationObjects([]);
      setAverageFieldObjects({});
      setSaveDisabled(false);
      setIsLoading(false);
      return;
    }
    formulaList = JSON.parse(formulaString);
    newFields = [];
    newOperationObjects = [];
    newAverageFieldObjects = {};
    fieldIndex = 0;
    formulaList.forEach((one, k) => {
      if (one.field) {
        newFields.push(one.field);
      } else if (one.operator) {
        if (one.operator === 'average') {
          newFields.push('average');
          uniqueID.current = 'uuid' + Math.random();
          newAverageFieldObjects[k / 2] = one.values.map(subfieldVal => {
            uniqueID.current = 'uuid' + Math.random();
            return {
              key: uniqueID.current,
              value: subfieldVal
            };
          });
        } else {
          uniqueID.current = 'uuid' + Math.random();
          newOperationObjects.push({
            key: uniqueID.current,
            value: one.operator
          });
        }
      }
    });
    setInitialFormulaArray(formulaList);
    setFormulaArray(formulaList);
    setSelectedFields(newFields);
    setSelectedOperationObjects(newOperationObjects);
    setAverageFieldObjects(newAverageFieldObjects);
    setSaveDisabled(false);
    setIsLoading(false);
  }, []);

  const idsOfDisallowedParentTypes = allFields
    .filter(one => SUBFIELDS_NOT_ALLOWED.includes(one.uiType))
    .map(one => one.internalId);

  let qIndex = 0;

  let fieldDropDownOptions = allFields
    .filter(one => {
      const parentFieldId = one?.lookupInfo?.subFieldInfo?.parentFieldId;
      let parentField;
      if (parentFieldId) {
        parentField = allFields.find(p => p.internalId === parentFieldId);
        if (
          parentField.uiType === 'credas' &&
          one.lookupInfo.subFieldInfo.externalFormFieldId === 'country'
        ) {
          one.parentTitle = parentField.title;
          return true;
        }
        if (
          parentField.uiType === 'worldcheck' ||
          parentField.uiType === 'worldcheck_org'
        ) {
          if (
            ALLOWED_WORLDCHECK_SUBFIELDS.includes(
              one.lookupInfo.subFieldInfo.externalFormFieldId
            )
          ) {
            one.parentTitle = parentField.title;
            return true;
          }
        }
      }
      return (
        one.internalId !== internalId &&
        UI_TYPES_ALLOWED_FOR_SCORING.includes(one.uiType) &&
        (!one.lookupInfo ||
          !one.lookupInfo.subFieldInfo ||
          !one.lookupInfo.subFieldInfo.parentFieldId ||
          !idsOfDisallowedParentTypes.includes(
            one.lookupInfo.subFieldInfo.parentFieldId
          ))
      );
    })
    .map(one => {
      if (one.uiType === 'section_break') {
        return {
          text: one.title,
          value: one.internalId,
          qLabel: '',
          uiType: 'section_break'
        };
      } else {
        qIndex += 1;
        return {
          text: one.parentTitle
            ? one.parentTitle + ' -> ' + one.title
            : one.title,
          value: one.internalId,
          qLabel: 'Q' + qIndex
        };
      }
    });
  let finalFieldIndex = fieldDropDownOptions.length - 1;
  fieldDropDownOptions = fieldDropDownOptions.filter((one, index) => {
    if (one.uiType === 'section_break') {
      if (index === finalFieldIndex) {
        return false;
      }
      if (fieldDropDownOptions[index + 1].uiType === 'section_break') {
        return false;
      }
    }
    return true;
  });

  const clickedSave = () => {
    if (!validateFormula()) {
      return;
    }
    autosaveField(UPDATE_SCORE_FIELD, {
      formula: JSON.stringify(formulaArray)
    });
    setValidationError(false);
    setIsLoading(true);
    setTimeout(() => {
      setShowScoreDialog(false);
      setIsLoading(false);
    }, 1000);
  };

  const numberButtons = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  const operatorButtons = ['add', 'subtract', 'multiply', 'divide'];
  const functionButtons = ['min', 'max', 'avg'];
  const bracketButtons = ['openBrackets', 'closeBrackets'];

  const validateFormula = () => {
    const lastItem = formulaArray[formulaArray.length - 1];

    if (
      lastItem &&
      lastItem.operator &&
      lastItem.operator !== 'closeBrackets' &&
      (!lastItem.values || !lastItem.values.length)
    ) {
      setValidationError('ending_in_operator');
      return false;
    }
    const openBracketCount = formulaArray.filter(
      one => one.operator === 'openBrackets'
    ).length;
    const closeBracketCount = formulaArray.filter(
      one => one.operator === 'closeBrackets'
    ).length;
    if (openBracketCount > closeBracketCount) {
      setValidationError('mismatched_brackets');
      return false;
    }
    if (closeBracketCount > openBracketCount) {
      setValidationError('mismatched_closing_brackets');
      return false;
    }
    let previousItem = null;
    let passedTheseRules = formulaArray.every((one, k) => {
      if (
        one.operator &&
        operatorButtons.includes(one.operator) &&
        one.operator !== 'openBrackets' &&
        !previousItem
      ) {
        setValidationError('starting_with_operator');
        return false;
      }
      if (
        one.operator &&
        previousItem &&
        previousItem.operator &&
        operatorButtons.includes(previousItem.operator) &&
        operatorButtons.includes(one.operator)
      ) {
        setValidationError('two_operators_in_a_row');
        return false;
      }
      if (
        (previousItem &&
          one.operator === 'openBrackets' &&
          previousItem.operator === 'closeBrackets') ||
        (one.operator === 'closeBrackets' &&
          previousItem.operator === 'openBrackets')
      ) {
        setValidationError('mismatched_brackets2');
        return false;
      }
      if (
        previousItem &&
        (previousItem.value || previousItem.values || previousItem.field) &&
        (one.value || one.values || one.field)
      ) {
        setValidationError('two_values_in_a_row');
        return false;
      }
      if (one.values && !one.values.length) {
        setValidationError('empty_function');
        return false;
      }
      previousItem = one;
      return true;
    });
    if (!passedTheseRules) {
      return false;
    }

    return true;
  };

  const onChangedFormula = formula => {
    setValidationError(false);
    setFormulaArray(formula);
  };
  const viewProps = {
    allFields,
    averageFieldObjects,
    clickedSave,
    fieldDropDownOptions,
    saveDisabled,
    selectedFields,
    setAverageFieldObjects,
    setSelectedFields,
    selectedOperationObjects,
    setSelectedOperationObjects,
    setSaveDisabled,
    setShowScoreDialog,
    onChangedFormula,
    initialFormulaArray,
    validationError,
    ...props
  };

  return <ScoreSettingsPanelView {...viewProps} />;
};

export default ScoreSettingsPanelEngine;
